import React, { useState } from "react";
import {
  FaUpload,
  FaCheckCircle,
  FaExclamationCircle,
  FaFileAlt,
  FaArrowLeft,
  FaInfoCircle,
  FaUniversity,
  FaPassport,
  FaTimes,
} from "react-icons/fa";
import { findStudentOrAppointmentByNumber, uploadDocument } from "../firebase";
import "./DocumentUpload.css";

const DocumentUpload = ({ onBackToHome }) => {
  const [studentNumber, setStudentNumber] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Document categories with required files
  const documentCategories = {
    okul: {
      title: "Okul İçin Gerekli Belgeler",
      icon: <FaUniversity />,
      documents: [
        { id: "lise-transkript-tr", name: "Lise Transkript (Türkçe)" },
        { id: "lise-transkript-de", name: "Lise Transkript (Almanca Çeviri)" },
        { id: "osym-kabul-tr", name: "ÖSYM Kabul Belgesi (Türkçe)" },
        { id: "osym-kabul-de", name: "ÖSYM Kabul Belgesi (Almanca Çeviri)" },
        { id: "dil-belgesi", name: "Dil Belgesi (TestDaF/DSH/Goethe)" },
        { id: "diploma", name: "Lise Diploması" },
      ],
    },
    vize: {
      title: "Vize İçin Gerekli Belgeler",
      icon: <FaPassport />,
      documents: [
        { id: "pasaport", name: "Pasaport Fotokopisi" },
        { id: "biyometrik-foto", name: "Biyometrik Fotoğraf" },
        { id: "kabul-mektubu", name: "Üniversite Kabul Mektubu" },
        { id: "bloke-hesap", name: "Bloke Hesap Belgesi" },
        { id: "sigorta", name: "Sağlık Sigortası Belgesi" },
        { id: "ikametgah", name: "İkametgah Belgesi" },
      ],
    },
  };

  const [selectedFiles, setSelectedFiles] = useState({
    okul: {},
    vize: {},
  });

  const handleStudentNumberChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (value.length <= 8) {
      setStudentNumber(value);
      setError("");
    }
  };

  const handleVerifyStudent = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      if (studentNumber.trim().length !== 8) {
        throw new Error("Öğrenci numarası 8 karakterden oluşmalıdır");
      }

      const result = await findStudentOrAppointmentByNumber(studentNumber.trim());

      if (!result) {
        setError(
          "Bu numaraya ait kayıt bulunamadı. Lütfen numaranızı kontrol edin."
        );
      } else {
        setStudentData(result);
        setIsVerified(true);
      }
    } catch (err) {
      console.error("Verification error:", err);
      setError(
        err.message || "Doğrulama sırasında bir hata oluştu. Lütfen tekrar deneyin."
      );
    }

    setIsLoading(false);
  };

  const handleFileSelect = (category, docId, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("Dosya boyutu 10MB'dan küçük olmalıdır");
        return;
      }

      setSelectedFiles((prev) => ({
        ...prev,
        [category]: {
          ...prev[category],
          [docId]: file,
        },
      }));
      setError("");
    }
  };

  const handleRemoveFile = (category, docId) => {
    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[category][docId];
      return newFiles;
    });
  };

  const handleUploadDocument = async (category, docId, docName) => {
    const file = selectedFiles[category][docId];
    if (!file) return;

    setUploadProgress((prev) => ({
      ...prev,
      [`${category}-${docId}`]: "uploading",
    }));

    try {
      const fileName = `${docName}_${Date.now()}.${file.name.split(".").pop()}`;
      await uploadDocument(studentNumber, category, fileName, file);

      setUploadProgress((prev) => ({
        ...prev,
        [`${category}-${docId}`]: "success",
      }));

      // Remove from selected files after successful upload
      handleRemoveFile(category, docId);

      setSuccessMessage(`${docName} başarıyla yüklendi!`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Upload error:", err);
      setUploadProgress((prev) => ({
        ...prev,
        [`${category}-${docId}`]: "error",
      }));
      setError(`${docName} yüklenirken hata oluştu: ${err.message}`);
    }
  };

  const getUploadStatus = (category, docId) => {
    return uploadProgress[`${category}-${docId}`] || "idle";
  };

  const resetForm = () => {
    setStudentNumber("");
    setIsVerified(false);
    setStudentData(null);
    setSelectedFiles({ okul: {}, vize: {} });
    setUploadProgress({});
    setError("");
    setSuccessMessage("");
  };

  if (!isVerified) {
    return (
      <div className="docupload-page">
        <div className="docupload-background"></div>

        <button className="docupload-back-button" onClick={onBackToHome}>
          <FaArrowLeft />
          Ana Sayfaya Dön
        </button>

        <div className="docupload-container">
          <div className="docupload-header">
            <div className="docupload-logo">
              <FaFileAlt />
            </div>
            <h1>Belge Yükleme</h1>
            <p>
              Öğrenci numaranızı girerek belgelerinizi yükleyebilirsiniz.
            </p>
          </div>

          <div className="docupload-verify-section">
            <form onSubmit={handleVerifyStudent} className="docupload-verify-form">
              <div className="docupload-form-group">
                <label htmlFor="studentNumber">Öğrenci Numaranız</label>
                <div className="docupload-input-container">
                  <input
                    type="text"
                    id="studentNumber"
                    value={studentNumber}
                    onChange={handleStudentNumberChange}
                    placeholder="Örn: AB12CD34"
                    maxLength="8"
                    required
                    className={error ? "error" : ""}
                  />
                </div>
                <small className="docupload-input-help">
                  Öğrenci numaranız 8 haneli harf ve rakam kombinasyonudur
                </small>
              </div>

              {error && (
                <div className="docupload-error-message">
                  <FaExclamationCircle />
                  <span>{error}</span>
                </div>
              )}

              <div className="docupload-form-actions">
                <button
                  type="submit"
                  className="docupload-verify-button"
                  disabled={isLoading || studentNumber.length !== 8}
                >
                  {isLoading ? "Doğrulanıyor..." : "Devam Et"}
                </button>
              </div>
            </form>

            <div className="docupload-info-box">
              <FaInfoCircle />
              <div>
                <strong>Öğrenci numaranızı nereden bulabilirsiniz?</strong>
                <p>
                  Öğrenci numaranız, size e-posta veya SMS ile gönderilmiştir.
                  Eğer bulamıyorsanız, lütfen danışmanınızla iletişime geçin.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="docupload-page">
      <div className="docupload-background"></div>

      <button className="docupload-back-button" onClick={resetForm}>
        <FaArrowLeft />
        Geri Dön
      </button>

      <div className="docupload-container">
        <div className="docupload-header">
          <div className="docupload-logo success">
            <FaCheckCircle />
          </div>
          <h1>Hoş Geldiniz, {studentData?.userData?.firstName}!</h1>
          <p>Aşağıdaki belgelerinizi kategorilere göre yükleyebilirsiniz.</p>
        </div>

        {successMessage && (
          <div className="docupload-success-message">
            <FaCheckCircle />
            <span>{successMessage}</span>
          </div>
        )}

        {error && (
          <div className="docupload-error-message">
            <FaExclamationCircle />
            <span>{error}</span>
          </div>
        )}

        {Object.entries(documentCategories).map(([category, categoryData]) => (
          <div key={category} className="docupload-category">
            <div className="docupload-category-header">
              {categoryData.icon}
              <h2>{categoryData.title}</h2>
            </div>

            <div className="docupload-checklist">
              {categoryData.documents.map((doc) => {
                const status = getUploadStatus(category, doc.id);
                const selectedFile = selectedFiles[category][doc.id];

                return (
                  <div key={doc.id} className="docupload-item">
                    <div className="docupload-item-header">
                      <label htmlFor={`${category}-${doc.id}`}>
                        {doc.name}
                      </label>
                      {status === "success" && (
                        <FaCheckCircle className="status-icon success" />
                      )}
                      {status === "error" && (
                        <FaExclamationCircle className="status-icon error" />
                      )}
                    </div>

                    <div className="docupload-item-actions">
                      {!selectedFile ? (
                        <label
                          htmlFor={`${category}-${doc.id}`}
                          className="docupload-file-label"
                        >
                          <input
                            type="file"
                            id={`${category}-${doc.id}`}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) =>
                              handleFileSelect(category, doc.id, e)
                            }
                            disabled={status === "uploading"}
                          />
                          Dosya Seç
                        </label>
                      ) : (
                        <>
                          <div className="docupload-selected-file">
                            <FaFileAlt />
                            <span>{selectedFile.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(category, doc.id)}
                              className="docupload-remove-button"
                              disabled={status === "uploading"}
                            >
                              <FaTimes />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handleUploadDocument(category, doc.id, doc.name)
                            }
                            className="docupload-upload-button"
                            disabled={status === "uploading"}
                          >
                            <FaUpload />
                            {status === "uploading" ? "Yükleniyor..." : "Yükle"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="docupload-help-section">
          <div className="docupload-help-card">
            <FaInfoCircle className="help-icon" />
            <div className="help-content">
              <h4>Yardıma mı ihtiyacınız var?</h4>
              <p>
                Belge yükleme ile ilgili sorularınız için danışmanlarımızla
                iletişime geçebilirsiniz.
              </p>
              <div className="contact-info">
                <span>📞 +49 152 313 90 961</span>
                <span>📧 bilgi@almanyakampus.com</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;